import React, { useState, useEffect } from 'react';
import { XMarkIcon, PencilIcon, TrashIcon, PlusIcon } from '@heroicons/react/24/outline';
import { notify } from '../utils/notifications';
import { useMutation } from '../hooks/useApi';
import { formatDate } from '../utils/dateUtils';

const PackageManagementModal = ({ onClose, onSuccess }) => {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    bandwidth_mbps: '',
    price: '',
    description: '',
    is_active: true
  });

  const { mutate: createPackage, loading: createLoading } = useMutation(
    (data) => fetch('/api/packages', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
      body: JSON.stringify(data)
    }).then(res => res.json()),
    {
      onSuccess: () => {
        notify.success('Package created successfully!');
        setShowForm(false);
        resetForm();
        fetchPackages();
        onSuccess && onSuccess();
      },
      onError: (error) => {
        const errorMessage = error?.response?.data?.message || error?.message || 'Failed to create package';
        notify.error(errorMessage);
      }
    }
  );

  const { mutate: updatePackage, loading: updateLoading } = useMutation(
    ({ id, data }) => fetch(`/api/packages/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
      body: JSON.stringify(data)
    }).then(res => res.json()),
    {
      onSuccess: () => {
        notify.success('Package updated successfully!');
        setShowForm(false);
        setEditingPackage(null);
        resetForm();
        fetchPackages();
        onSuccess && onSuccess();
      },
      onError: (error) => {
        const errorMessage = error?.response?.data?.message || error?.message || 'Failed to update package';
        notify.error(errorMessage);
      }
    }
  );

  const { mutate: deletePackage, loading: deleteLoading } = useMutation(
    (id) => fetch(`/api/packages/${id}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    }).then(res => res.json()),
    {
      onSuccess: () => {
        notify.success('Package deleted successfully!');
        fetchPackages();
        onSuccess && onSuccess();
      },
      onError: (error) => {
        const errorMessage = error?.response?.data?.message || error?.message || 'Failed to delete package';
        notify.error(errorMessage);
      }
    }
  );

  const fetchPackages = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/packages');
      const result = await response.json();
      
      if (result.success) {
        setPackages(result.data);
      } else {
        notify.error('Failed to load packages');
      }
    } catch (error) {
      console.error('Error fetching packages:', error);
      notify.error('Failed to load packages');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPackages();
  }, []);

  const resetForm = () => {
    setFormData({
      name: '',
      bandwidth_mbps: '',
      price: '',
      description: '',
      is_active: true
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (editingPackage) {
      updatePackage({ id: editingPackage.id, data: formData });
    } else {
      createPackage(formData);
    }
  };

  const handleEdit = (pkg) => {
    setEditingPackage(pkg);
    setFormData({
      name: pkg.name,
      bandwidth_mbps: pkg.bandwidth_mbps,
      price: pkg.price,
      description: pkg.description || '',
      is_active: pkg.is_active
    });
    setShowForm(true);
  };

  const handleDelete = (pkg) => {
    if (window.confirm(`Are you sure you want to delete package "${pkg.name}"? This action cannot be undone.`)) {
      deletePackage(pkg.id);
    }
  };

  const cancelForm = () => {
    setShowForm(false);
    setEditingPackage(null);
    resetForm();
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price);
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-6xl shadow-lg rounded-md bg-white">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">Manage Bandwidth Packages</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {!showForm ? (
          <>
            <div className="mb-4 flex justify-end">
              <button
                onClick={() => setShowForm(true)}
                className="btn-primary flex items-center"
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                Add New Package
              </button>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Package Details
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Bandwidth
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Price
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Created
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {packages.map((pkg) => (
                      <tr key={pkg.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {pkg.name}
                            </div>
                            <div className="text-sm text-gray-500">
                              {pkg.description}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {pkg.formatted_display}
                          </div>
                          <div className="text-sm text-gray-500">
                            {pkg.bandwidth_mbps} Mbps
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {pkg.formatted_price}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            pkg.is_active
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {pkg.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {formatDate(pkg.createdAt)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => handleEdit(pkg)}
                              className="text-blue-600 hover:text-blue-900"
                              title="Edit Package"
                            >
                              <PencilIcon className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(pkg)}
                              disabled={deleteLoading}
                              className="text-red-600 hover:text-red-900 disabled:opacity-50"
                              title="Delete Package"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        ) : (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h4 className="text-lg font-medium text-gray-900">
                {editingPackage ? 'Edit Package' : 'Create New Package'}
              </h4>
              <button
                onClick={cancelForm}
                className="btn-secondary"
              >
                Cancel
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="form-label">Package Name *</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    required
                    placeholder="e.g., Premium 500"
                  />
                </div>
                <div>
                  <label className="form-label">Bandwidth (Mbps) *</label>
                  <input
                    type="number"
                    min="1"
                    className="form-input"
                    value={formData.bandwidth_mbps}
                    onChange={(e) => setFormData({...formData, bandwidth_mbps: e.target.value})}
                    required
                    placeholder="e.g., 500"
                  />
                </div>
                <div>
                  <label className="form-label">Price (IDR) *</label>
                  <input
                    type="number"
                    min="0"
                    className="form-input"
                    value={formData.price}
                    onChange={(e) => setFormData({...formData, price: e.target.value})}
                    required
                    placeholder="e.g., 10300000"
                  />
                  {formData.price && (
                    <p className="mt-1 text-sm text-gray-500">
                      {formatPrice(parseInt(formData.price))}
                    </p>
                  )}
                </div>
                <div>
                  <label className="form-label">Status</label>
                  <select
                    className="form-input"
                    value={formData.is_active}
                    onChange={(e) => setFormData({...formData, is_active: e.target.value === 'true'})}
                  >
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="form-label">Description</label>
                <textarea
                  rows={3}
                  className="form-input"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Optional description for this package"
                />
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={cancelForm}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createLoading || updateLoading}
                  className="btn-primary disabled:opacity-50"
                >
                  {(createLoading || updateLoading) ? 'Saving...' : (editingPackage ? 'Update Package' : 'Create Package')}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default PackageManagementModal;